import { randRange } from "../core/math.js";
import { ensureWeapon } from "./weapons.js";

export function createPlayer(hero) {
  const p = {
    heroId: hero?.id ?? "runner",
    glyph: hero?.glyph ?? "@",
    range: hero?.range ?? 120,
    aimAngle: 0,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    lastMoveX: 1,
    lastMoveY: 0,
    r: 8,
    hp: hero?.hpMax ?? 100,
    hpMax: hero?.hpMax ?? 100,
    speed: hero?.speed ?? 175,
    xp: 0,
    level: 1,
    // weapons are stored as instances
    weapons: [
      {
        id: "pistol",
        cd: 0,
        lvl: 1,
      },
    ],
    // temporary buffs
    buffs: {
      fireRateMul: hero?.buffs?.fireRateMul ?? 1,
      moveSpeedMul: hero?.buffs?.moveSpeedMul ?? 1,
      dmgMul: hero?.buffs?.dmgMul ?? 1,
      // gameplay perks (small, stackable)
      ricochetChanceAdd: 0, // added to base wall ricochet chance
      magnetMul: 1,
      dashCdMul: 1,
      dashPowMul: 1,
      // synergies
      critChance: 0,
      critMul: 1.6,
      critBurn: false,
      executeMul: 1,
      burnSpread: 0,
    },
  };
  // apply start weapons
  const ws = hero?.startWeapons || ["pistol"];
  for (let i = 0; i < ws.length; i++) ensureWeapon(p, ws[i]);
  // align hp
  p.hp = p.hpMax;
  return p;
}

export function createBullet({
  x,
  y,
  vx,
  vy,
  dmg,
  ttl = 1.1,
  r = 3,
  pierce = 0,
  knock = 0,
  bleedDps = 0,
  bleedT = 0,
  kind = "bullet",
  ...rest
}) {
  return { x, y, vx, vy, dmg, ttl, r, pierce, knock, bleedDps, bleedT, kind, ...rest };
}

export function createPickup({ x, y, kind, value = 1 }) {
  return { x, y, kind, value, ttl: 16 };
}

export function createEnemy({ x, y, kind, wave, diff, bossType, affix }) {
  const mHp = diff?.enemyHpMul ?? 1;
  const mSp = diff?.enemySpeedMul ?? 1;
  const aff = affix || null;
  if (kind === "fast") {
    const hpMax = (22 + wave * 4 + randRange(0, 10)) * mHp;
    return {
      kind,
      x,
      y,
      vx: 0,
      vy: 0,
      r: 8,
      hp: hpMax,
      hpMax,
      speed: (78 + wave * 3.4 + randRange(0, 12)) * mSp,
      dmgMul: 0.9,
      contactMul: 1.22,
      xp: 1,
      isBoss: false,
      affix: aff,
    };
  }
  if (kind === "tank") {
    const hpMax = (62 + wave * 10 + randRange(0, 20)) * mHp;
    return {
      kind,
      x,
      y,
      vx: 0,
      vy: 0,
      r: 11,
      hp: hpMax,
      hpMax,
      speed: (34 + wave * 1.4 + randRange(0, 6)) * mSp,
      dmgMul: 1.25,
      contactMul: 1.36,
      xp: 3,
      isBoss: false,
      affix: aff,
    };
  }
  if (kind === "spitter") {
    const hpMax = (36 + wave * 7 + randRange(0, 14)) * mHp;
    return {
      kind,
      x,
      y,
      vx: 0,
      vy: 0,
      r: 10,
      hp: hpMax,
      hpMax,
      speed: (46 + wave * 2.0 + randRange(0, 8)) * mSp,
      dmgMul: 1,
      contactMul: 0.52,
      xp: 2,
      isBoss: false,
      affix: aff,
      shootCd: randRange(0.2, 1.0),
      // telegraph before shooting
      windT: 0,
      windDx: 0,
      windDy: 0,
      windDmg: 0,
    };
  }
  if (kind === "shield") {
    const hpMax = (56 + wave * 9 + randRange(0, 18)) * mHp;
    return {
      kind,
      x,
      y,
      vx: 0,
      vy: 0,
      r: 12,
      hp: hpMax,
      hpMax,
      speed: (34 + wave * 1.6 + randRange(0, 6)) * mSp,
      dmgMul: 1.15,
      contactMul: 1.28,
      xp: 3,
      isBoss: false,
      affix: aff,
      shieldMul: 0.42, // frontal damage multiplier
    };
  }
  if (kind === "charger") {
    const hpMax = (40 + wave * 7 + randRange(0, 14)) * mHp;
    return {
      kind,
      x,
      y,
      vx: 0,
      vy: 0,
      r: 10,
      hp: hpMax,
      hpMax,
      speed: (52 + wave * 2.2 + randRange(0, 10)) * mSp,
      dmgMul: 1.1,
      contactMul: 1.45,
      xp: 2,
      isBoss: false,
      affix: aff,
      chargeCd: randRange(0.8, 1.8),
      chargeWindT: 0,
      chargeT: 0,
      chargeDx: 0,
      chargeDy: 0,
    };
  }
  if (kind === "exploder") {
    const hpMax = (26 + wave * 5 + randRange(0, 10)) * mHp;
    return {
      kind,
      x,
      y,
      vx: 0,
      vy: 0,
      r: 10,
      hp: hpMax,
      hpMax,
      speed: (66 + wave * 2.6 + randRange(0, 12)) * mSp,
      dmgMul: 1.0,
      contactMul: 0.8,
      xp: 2,
      isBoss: false,
      affix: aff,
      boomR: 42,
      boomDmg: 26 + wave * 1.2,
    };
  }
  if (kind === "summoner") {
    const hpMax = (44 + wave * 8 + randRange(0, 16)) * mHp;
    return {
      kind,
      x,
      y,
      vx: 0,
      vy: 0,
      r: 11,
      hp: hpMax,
      hpMax,
      speed: (30 + wave * 1.2 + randRange(0, 6)) * mSp,
      dmgMul: 1.0,
      contactMul: 0.95,
      xp: 3,
      isBoss: false,
      affix: aff,
      summonCd: randRange(1.2, 2.6),
    };
  }
  if (kind === "grenadier") {
    const hpMax = (34 + wave * 7 + randRange(0, 14)) * mHp;
    return {
      kind,
      x,
      y,
      vx: 0,
      vy: 0,
      r: 10,
      hp: hpMax,
      hpMax,
      speed: (28 + wave * 1.1 + randRange(0, 6)) * mSp,
      dmgMul: 1.0,
      contactMul: 0.5,
      xp: 3,
      isBoss: false,
      affix: aff,
      throwCd: randRange(0.8, 2.0),
      throwWindT: 0,
      throwTx: 0,
      throwTy: 0,
    };
  }
  if (kind === "pyro") {
    const hpMax = (38 + wave * 7 + randRange(0, 14)) * mHp;
    return {
      kind,
      x,
      y,
      vx: 0,
      vy: 0,
      r: 10,
      hp: hpMax,
      hpMax,
      speed: (34 + wave * 1.4 + randRange(0, 6)) * mSp,
      dmgMul: 1.0,
      contactMul: 0.65,
      xp: 3,
      isBoss: false,
      affix: aff,
      fireCd: randRange(1.0, 2.4),
      fireWindT: 0,
      fireTx: 0,
      fireTy: 0,
    };
  }
  if (kind === "boss") {
    const bt = bossType || "summoner";
    // Boss scaling: stronger later (linear + quadratic), still affected by difficulty multipliers.
    const hpBase = 520 + wave * 200 + wave * wave * 4.2;
    const hpMax = hpBase * mHp * (bt === "sack" ? 2.35 : bt === "titan" ? 1.35 : bt === "rager" ? 1.05 : 1);
    const spBase = 26 + wave * 1.0;
    const speed = spBase * mSp * (bt === "sack" ? 0.85 : bt === "rager" ? 1.25 : bt === "artillery" ? 0.95 : 1);
    const dmgMul = (2.1 + wave * 0.03) * (bt === "sack" ? 1.15 : bt === "titan" ? 1.25 : 1);
    return {
      kind,
      bossType: bt,
      x,
      y,
      vx: 0,
      vy: 0,
      r: bt === "sack" ? 26 : bt === "titan" ? 22 : 18,
      hp: hpMax,
      hpMax,
      speed,
      dmgMul,
      contactMul: bt === "artillery" ? 0.95 : bt === "sack" ? 1.7 : bt === "titan" ? 1.55 : 1.35,
      xp: bt === "sack" ? 40 : 24,
      isBoss: true,
      affix: null,
      // shared boss cooldowns (used by patterns in combat.js)
      bossCd: randRange(0.8, 1.6),
      summonCd: 2.5,
      // rager pattern
      chargeCd: randRange(0.8, 1.6),
      chargeWindT: 0,
      chargeT: 0,
      chargeDx: 0,
      chargeDy: 0,
    };
  }
  // default walker
  const hpMax = (32 + wave * 6 + randRange(0, 10)) * mHp;
  return {
    kind: "walker",
    x,
    y,
    vx: 0,
    vy: 0,
    r: 9,
    hp: hpMax,
    hpMax,
    speed: (46 + wave * 2.2 + randRange(0, 10)) * mSp,
    dmgMul: 1,
    contactMul: 1.15,
    xp: 1,
    isBoss: false,
    affix: aff,
  };
}
